import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Dropdown,
  DropdownItem,
  Separator,
  Form,
  FormButton,
  FormSubmitHandler,
  DataStatus,
} from 'platform/components';
import {Box, Heading, HStack, Icon, Show, ThemeIconKey, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {Fragment} from 'react';

import {groupBy} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  PostServiceCaseIntegrationsXentryOrderSynchronizationApiResponse,
  usePostServiceCaseIntegrationsXentryOrderSynchronizationApiMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {suffixTestId, RequiredTestIdProps} from 'shared/web';

import {DataCard} from './components/DataCard';
import {SOURCES} from './constants/Sources';
import {FormSchema} from './types/FormSchema';

interface SyncXentryProps extends RequiredTestIdProps {
  serviceCaseId: string;
  groups: NonNullable<
    NonNullable<PostServiceCaseIntegrationsXentryOrderSynchronizationApiResponse>['groups']
  >;
}

export function SyncXentry(props: SyncXentryProps) {
  const [
    postServiceCaseIntegrationsXentryOrderSynchronization,
    {isLoading, data: xentryIntegrationSynchronization},
  ] = usePostServiceCaseIntegrationsXentryOrderSynchronizationApiMutation();

  const groups = xentryIntegrationSynchronization?.groups ?? props.groups;

  const groupedGroups = Object.entries(
    groupBy((group) => group?.type ?? 'unknown', groups)
  ).flatMap(([type, values]) => {
    const conflicts = [];
    for (let i = 0; i < values.length; i += 2) {
      const firstGroup = values[i];
      const secondGroup = values[i + 1];
      if (firstGroup || secondGroup) {
        conflicts.push({
          type,
          values: [firstGroup, secondGroup].filter(Boolean),
          conflictIndex: i / 2,
        });
      }
    }
    return conflicts;
  });

  const handleSubmit: FormSubmitHandler<FormSchema> = async (data) => {
    const selectedGroups = data.groups.map((dataGroup) => {
      const conflictGroups = groups.filter((group) => group?.type === dataGroup.type);
      const conflictPairStart = dataGroup.conflictIndex * 2;
      const relevantGroups = conflictGroups.slice(conflictPairStart, conflictPairStart + 2);

      return (
        relevantGroups.find((group) => group?.selectedSource === dataGroup.selectedSource) ?? null
      );
    });

    await postServiceCaseIntegrationsXentryOrderSynchronization({
      body: {groups: selectedGroups},
      serviceCaseId: props.serviceCaseId,
    })
      .unwrap()
      .then(closeCurrentDialog)
      .catch(handleApiError);
  };

  const defaultFormValues: FormSchema = {
    groups: groupedGroups.map((conflict) => ({
      type: conflict.type,
      selectedSource: SOURCES.OMNETIC,
      conflictIndex: conflict.conflictIndex,
    })),
  };

  return (
    <Form<FormSchema> defaultValues={defaultFormValues} onSubmit={handleSubmit}>
      {(control, formApi) => {
        const selectAll = (source: string) =>
          formApi.setValue(
            'groups',
            groupedGroups.map((conflict) => ({
              type: conflict.type,
              selectedSource: source,
              conflictIndex: conflict.conflictIndex,
            }))
          );
        return (
          <VStack spacing={1}>
            <Box>
              <HStack spacing={2}>
                <Dropdown
                  dropdownControl={
                    <Button
                      variant="link"
                      leftIcon="action/done_all"
                      title={i18n.t('general.actions.selectAll')}
                    />
                  }
                >
                  <DropdownItem
                    label={i18n.t('entity.integration.labels.omnetic')}
                    onClick={() => selectAll(SOURCES.OMNETIC)}
                  />
                  <DropdownItem
                    label={i18n.t('entity.integration.labels.xentry')}
                    onClick={() => selectAll(SOURCES.XENTRY)}
                  />
                </Dropdown>
                <Button
                  variant="link"
                  leftIcon="navigation/refresh"
                  onClick={() =>
                    postServiceCaseIntegrationsXentryOrderSynchronization({
                      serviceCaseId: props.serviceCaseId,
                    })
                  }
                  isLoading={isLoading}
                  title={i18n.t('general.actions.refresh')}
                />
              </HStack>
              <Separator />
            </Box>
            <DataStatus
              isEmpty={isNilOrEmpty(groups)}
              emptyMessage={i18n.t('entity.integration.notifications.noData')}
            >
              <VStack spacing={4}>
                {groupedGroups?.map(({type, values, conflictIndex}, index) => {
                  const firstGroup = values?.[0];
                  const secondGroup = values?.[1];

                  return (
                    <Fragment key={`${type}-${conflictIndex}`}>
                      <HStack align="center" spacing={4}>
                        <Box
                          width={10}
                          height={10}
                          borderRadius="medium"
                          backgroundColor="palettes.blue.20.100"
                          padding={2}
                        >
                          <Icon
                            value={match<string, ThemeIconKey | undefined>(type)
                              .with('checkin', () => 'navigation/arrow_upward')
                              .with('handover', () => 'navigation/arrow_downward')
                              .with('service-order', () => 'sidebar/workshop_service_orders')
                              .with('service-campaign', () => 'sidebar/workshop_service_campaigns')
                              .otherwise(() => undefined)}
                            color="palettes.blue.80.100"
                          />
                        </Box>
                        <Heading size={4}>
                          {match(type)
                            .with('checkin', () => i18n.t('entity.workshop.labels.checkIn'))
                            .with('handover', () => i18n.t('entity.workshop.labels.handover'))
                            .with('service-order', () => values?.[0]?.name)
                            .with('service-campaign', () =>
                              i18n.t('entity.campaigns.labels.serviceCampaign')
                            )
                            .otherwise(() => type)}
                        </Heading>
                      </HStack>
                      <HStack spacing={4} width="100%">
                        <Show when={firstGroup?.isVisibleForUser} key={firstGroup?.attributes}>
                          <Box width="100%">
                            <DataCard
                              index={index}
                              formApi={formApi}
                              control={control}
                              group={firstGroup!}
                              comparedGroup={secondGroup}
                              data-testid={suffixTestId('omnetic-card', props)}
                            />
                          </Box>
                        </Show>
                        <Show when={secondGroup?.isVisibleForUser} key={secondGroup?.attributes}>
                          <Box width="100%">
                            <DataCard
                              index={index}
                              formApi={formApi}
                              control={control}
                              group={secondGroup!}
                              comparedGroup={firstGroup}
                              data-testid={suffixTestId('omnetic-card', props)}
                            />
                          </Box>
                        </Show>
                      </HStack>
                    </Fragment>
                  );
                })}
              </VStack>
            </DataStatus>
            <Separator />
            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.discard')}
                onClick={closeCurrentDialog}
                variant="secondary"
                data-testid={suffixTestId('close', props)}
              />
              <FormButton
                type="submit"
                control={control}
                title={i18n.t('entity.integration.actions.syncNow')}
                isDisabled={isNilOrEmpty(groups)}
                variant="primary"
                data-testid={suffixTestId('link', props)}
              />
            </ButtonGroup>
          </VStack>
        );
      }}
    </Form>
  );
}
